/**
 * PluginManagerPanel — Per-channel AudioWorklet plugin manager.
 *
 * Shows:
 *   - Built-in plugins (Bitcrusher, Stutter, Ring Mod) — click "Add" to load
 *   - Custom URL loader — paste any AudioWorklet processor URL + processorName
 *   - Already-loaded plugin list with per-param sliders + remove button
 *
 * All plugins are real AudioWorkletNodes wired between FxChain.output and
 * sidechainGain via engine._rewirePluginChain(). They receive transport ticks
 * (`{type:"transport", tempo, beat, bar, currentTime, playing}`) at ~12 Hz so
 * they can rate-lock to the DAW clock.
 */

import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plug, Trash2, Sparkles, Plus, Link2, Crown, Lock, Upload, FileCode2, Star, Globe, EyeOff, Store, TrendingUp, Flame } from "lucide-react";
import { toast } from "sonner";
import { BUILT_IN_PLUGINS } from "@/lib/pluginRegistry";
import { getUserToken } from "@/lib/userToken";
import useAuth from "@/lib/useAuth";
import PricingModal from "@/components/PricingModal";
// iter-146 — extracted sub-components for maintainability.
import LoadedPluginCard from "@/components/plugins/LoadedPluginCard";

const API = process.env.REACT_APP_BACKEND_URL;

/** iter-146 — `PluginParamSlider`, `PresetMenu`, and `LoadedPluginCard`
 *  were extracted to `components/plugins/`. The parent imports them above. */

export default function PluginManagerPanel({ open, onClose, channelIndex, channel, engine }) {
  const { plan } = useAuth();
  // Use a real datetime comparison rather than relying on `days_remaining > 0`
  // (which rounds DOWN — a Pro user with <24h left would be wrongly treated as
  // free). The /billing/plan endpoint returns plan_expires_at as ISO string.
  const isPro =
    plan?.plan === "pro" &&
    plan?.plan_expires_at &&
    new Date(plan.plan_expires_at) > new Date();
  const [busy, setBusy] = useState(null);
  const [customUrl, setCustomUrl] = useState("");
  const [customName, setCustomName] = useState("");
  // iter-116 — community plugins (user-uploaded worklets). The list lives on
  // the backend keyed by user_token; the upload form below pushes a .js file
  // + metadata. Loaded plugins appear under the built-ins as a 3rd section.
  const [communityPlugins, setCommunityPlugins] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  // iter-118 — marketplace state. `libraryTab` switches the section between
  // the user's personal uploads ("library") and the global public market
  // ("market"). Marketplace plugins live in their own state so we don't
  // step on the library list when the user toggles between tabs.
  const [libraryTab, setLibraryTab] = useState("library");  // library | market
  const [marketPlugins, setMarketPlugins] = useState([]);
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketSort, setMarketSort] = useState("featured");  // featured | stars | recent | installs
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLabel, setUploadLabel] = useState("");
  const [uploadProcessorName, setUploadProcessorName] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadParamSchema, setUploadParamSchema] = useState("[]");
  const uploadFileRef = useRef(null);
  const [bump, setBump] = useState(0);  // force re-read of channel.plugins
  // When a free user taps a Pro plugin we open the PricingModal instead of
  // loading. Storing the trigger here keeps the modal state local to this
  // component (the PricingModal mounts a backdrop one z-level above us).
  const [pricingOpen, setPricingOpen] = useState(false);
  // iter-116 — hooks must all run on every render (rules of hooks). Move the
  // `if (!open) return null;` guard to JUST before the return JSX, after
  // every useState/useEffect/useRef. The early-return previously lived
  // here and broke React's hook-count contract once the community-plugin
  // useEffect was added below.
  const loaded = channel?.plugins || [];

  const handleAddBuiltIn = async (pkg) => {
    // Pro-gate: free users see the PricingModal instead of loading. Authed
    // pro users + anonymous users with a yet-unknown plan both proceed.
    if (pkg.pro && !isPro) {
      setPricingOpen(true);
      return;
    }
    setBusy(pkg.id);
    try {
      const id = await engine.loadPluginToChannel(channelIndex, pkg.url, {
        processorName: pkg.processorName,
        label: pkg.label,
      });
      // Push defaults so the UI reflects whatever the worklet initialised with.
      for (const p of pkg.params) {
        engine.setPluginParam(channelIndex, id, p.key, p.defaultValue);
      }
      setBump((b) => b + 1);
      toast.success("Plugin loaded", { description: `${pkg.label} is now in CH ${channelIndex + 1} signal chain.` });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[plugin] load failed:", e);
      toast.error("Could not load plugin", { description: e?.message || "Unknown error" });
    } finally {
      setBusy(null);
    }
  };

  const handleAddCustom = async () => {
    const url = customUrl.trim();
    if (!url) { toast.error("Plugin URL required"); return; }
    setBusy("custom");
    try {
      const id = await engine.loadPluginToChannel(channelIndex, url, {
        processorName: customName.trim() || undefined,
        label: customName.trim() || url.split("/").pop().replace(/\.js$/i, ""),
      });
      void id;
      setBump((b) => b + 1);
      setCustomUrl("");
      setCustomName("");
      toast.success("Custom plugin loaded", { description: `URL · ${url.slice(0, 48)}…` });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[plugin] custom load failed:", e);
      toast.error("Could not load plugin", { description: e?.message || "Verify the URL is accessible and processorName matches." });
    } finally {
      setBusy(null);
    }
  };

  // ─── iter-116 · community plugins ──────────────────────────────────────
  // Fetch current user's uploaded plugins when the panel opens.
  useEffect(() => {
    if (!open) return;
    setCommunityLoading(true);
    axios.get(`${API}/api/plugins/community?user_token=${encodeURIComponent(getUserToken())}`, { timeout: 8000 })
      .then((r) => setCommunityPlugins(r.data || []))
      .catch((e) => console.warn("[community-plugins] list failed", e?.message))
      .finally(() => setCommunityLoading(false));
  }, [open]);

  // iter-118 — fetch the public marketplace when the user opens the panel
  // OR switches to the Market tab OR changes the sort. Library fetch is
  // separate so the personal-uploads section paints immediately.
  useEffect(() => {
    if (!open || libraryTab !== "market") return;
    setMarketLoading(true);
    axios.get(`${API}/api/plugins/community/marketplace`, {
      params: { viewer_token: getUserToken(), sort: marketSort, limit: 50 },
      timeout: 10000,
    })
      .then((r) => setMarketPlugins(r.data || []))
      .catch((e) => console.warn("[market-plugins] list failed", e?.message))
      .finally(() => setMarketLoading(false));
  }, [open, libraryTab, marketSort]);

  const handleUpload = async () => {
    const file = uploadFileRef.current?.files?.[0];
    if (!file) { toast.error("Pick a .js or .zip file first"); return; }
    // iter-117 — when uploading a WAM v2 bundle (.zip), the backend can
    // auto-fill label + processorName from the manifest, so we only require
    // them up-front for raw .js uploads.
    const isBundle = /\.zip$/i.test(file.name);
    if (!isBundle && (!uploadLabel.trim() || !uploadProcessorName.trim())) {
      toast.error("Name + processorName required for raw .js uploads");
      return;
    }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", uploadLabel.trim());
      fd.append("processor_name", uploadProcessorName.trim());
      fd.append("description", uploadDescription.trim());
      fd.append("param_schema_json", uploadParamSchema.trim() || "[]");
      fd.append("user_token", getUserToken());
      const r = await axios.post(`${API}/api/plugins/community/upload`, fd, { timeout: 15000 });
      setCommunityPlugins((prev) => [r.data, ...prev]);
      // Reset form, keep the panel open so the user sees the new plugin land.
      setUploadLabel("");
      setUploadProcessorName("");
      setUploadDescription("");
      setUploadParamSchema("[]");
      if (uploadFileRef.current) uploadFileRef.current.value = "";
      setUploadOpen(false);
      toast.success("Plugin uploaded", {
        description: `${r.data.label} is now in your Community library — click Add to load on CH ${channelIndex + 1}.`,
      });
    } catch (e) {
      console.error("[community-plugins] upload failed", e);
      toast.error("Upload failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(null);
    }
  };

  const handleAddCommunity = async (cp, { incrementInstall = false } = {}) => {
    setBusy(cp.id);
    try {
      const url = `${API}/api/plugins/community/${cp.id}.js`;
      const id = await engine.loadPluginToChannel(channelIndex, url, {
        processorName: cp.processor_name,
        label: cp.label,
      });
      // If the uploader provided a param schema, push defaults so the
      // engine + UI agree on the starting state.
      for (const p of (cp.param_schema || [])) {
        if (p?.key && p?.defaultValue != null) {
          engine.setPluginParam(channelIndex, id, p.key, p.defaultValue);
        }
      }
      setBump((b) => b + 1);
      // iter-118 — when loading from the Market tab, fire-and-forget the
      // install counter so trending sort reflects real adoption.
      if (incrementInstall) {
        const fd = new FormData();
        fd.append("user_token", getUserToken());
        axios.post(`${API}/api/plugins/community/${cp.id}/install`, fd)
          .then((r) => setMarketPlugins((prev) => prev.map((p) => p.id === cp.id ? r.data : p)))
          .catch((e) => console.warn("[community-plugins] install increment failed", e?.message));
      }
      toast.success("Community plugin loaded", { description: `${cp.label} is now in CH ${channelIndex + 1} signal chain.` });
    } catch (e) {
      console.error("[community-plugins] add failed", e);
      toast.error("Could not load plugin", { description: e?.message || "Check the processor name + worklet syntax." });
    } finally {
      setBusy(null);
    }
  };

  // iter-118 — flip a personal plugin's visibility. Owner-only (backend
  // enforces). Optimistic UI: we flip the local state immediately, revert
  // if the request fails.
  const handleToggleVisibility = async (cp) => {
    const next = !cp.is_public;
    setCommunityPlugins((prev) => prev.map((p) => p.id === cp.id ? { ...p, is_public: next } : p));
    try {
      const fd = new FormData();
      fd.append("is_public", next ? "true" : "false");
      fd.append("user_token", getUserToken());
      const r = await axios.patch(`${API}/api/plugins/community/${cp.id}/visibility`, fd);
      setCommunityPlugins((prev) => prev.map((p) => p.id === cp.id ? r.data : p));
      toast.success(next ? "Published to marketplace" : "Made private", {
        description: next ? "Anyone can now find + star this plugin." : "Only you can see this plugin now.",
      });
    } catch (e) {
      // Revert the optimistic flip.
      setCommunityPlugins((prev) => prev.map((p) => p.id === cp.id ? { ...p, is_public: cp.is_public } : p));
      toast.error("Visibility change failed", { description: e?.response?.data?.detail || e?.message });
    }
  };

  // iter-118 — toggle a star on a marketplace plugin. Optimistic UI mirrors
  // visibility handler.
  const handleToggleStar = async (cp) => {
    const next = !cp.is_starred;
    setMarketPlugins((prev) => prev.map((p) => p.id === cp.id ? {
      ...p, is_starred: next, star_count: p.star_count + (next ? 1 : -1),
    } : p));
    try {
      const fd = new FormData();
      fd.append("user_token", getUserToken());
      const r = await axios.post(`${API}/api/plugins/community/${cp.id}/star`, fd);
      setMarketPlugins((prev) => prev.map((p) => p.id === cp.id ? r.data : p));
    } catch (e) {
      // Revert.
      setMarketPlugins((prev) => prev.map((p) => p.id === cp.id ? cp : p));
      toast.error("Could not star", { description: e?.response?.data?.detail || e?.message });
    }
  };

  const handleDeleteCommunity = async (cp) => {
    if (!window.confirm(`Delete "${cp.label}" from your community library? Already-loaded copies on channels keep working.`)) return;
    try {
      await axios.delete(`${API}/api/plugins/community/${cp.id}?user_token=${encodeURIComponent(getUserToken())}`);
      setCommunityPlugins((prev) => prev.filter((p) => p.id !== cp.id));
      toast.success("Plugin removed from library");
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e?.message });
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        // AnimatePresence's direct children must each carry a stable key;
        // without it, React/AnimatePresence emit a duplicate-key warning even
        // when the child is a single element.
        key="plugin-modal-backdrop"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid={`plugin-modal-backdrop-${channelIndex}`}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
          className="relative w-[min(96vw,820px)] max-h-[90vh] overflow-y-auto rounded-3xl border border-white/[0.1] bg-gradient-to-br from-zinc-900/95 to-zinc-950/95 shadow-[0_24px_64px_-16px_rgba(0,0,0,0.65)]"
          data-testid={`plugin-modal-${channelIndex}`}
        >
          <header className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-6 py-4">
            <div className="flex items-center gap-3">
              <Plug className="h-5 w-5 text-cyan-300" />
              <div>
                <h2 className="font-display text-[18px] font-semibold tracking-tight text-white">
                  Plugins · CH {String(channelIndex + 1).padStart(2, "0")}
                </h2>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                  Tempo-locked AudioWorklet plugins · real-time
                </p>
              </div>
            </div>
            <button
              type="button"
              data-testid="plugin-modal-close"
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-white/65 transition hover:bg-white/[0.1]"
              aria-label="Close plugins"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div className="grid gap-4 p-6 md:grid-cols-2">
            {/* Built-ins */}
            <section
              data-testid="plugin-built-in-list"
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4"
            >
              <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fuchsia-200/80">
                <Sparkles className="h-3 w-3" />
                Built-in plugins
              </p>
              <div className="space-y-2">
                {BUILT_IN_PLUGINS.map((pkg) => {
                  const proLocked = pkg.pro && !isPro;
                  return (
                    <button
                      type="button"
                      key={pkg.id}
                      data-testid={`plugin-builtin-add-${pkg.id}`}
                      onClick={() => handleAddBuiltIn(pkg)}
                      disabled={busy === pkg.id}
                      className={`group relative flex w-full items-start gap-3 rounded-xl border p-3 text-left transition disabled:cursor-wait disabled:opacity-60 ${
                        proLocked
                          ? "border-amber-400/30 bg-gradient-to-br from-amber-500/[0.08] to-rose-500/[0.04] hover:border-amber-400/55 hover:bg-amber-500/[0.16]"
                          : "border-white/[0.08] bg-white/[0.04] hover:border-cyan-400/40 hover:bg-cyan-500/[0.08]"
                      }`}
                    >
                      <span className={`mt-0.5 grid h-7 w-7 flex-shrink-0 place-items-center rounded-md ring-1 ${
                        proLocked
                          ? "bg-amber-500/[0.20] text-amber-200 ring-amber-400/40"
                          : "bg-cyan-500/[0.15] text-cyan-200 ring-cyan-400/30"
                      }`}>
                        {proLocked ? <Lock className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                      </span>
                      <span className="flex-1">
                        <span className="flex items-center gap-2">
                          <span className="font-display text-[13px] font-semibold text-white">{pkg.label}</span>
                          {pkg.pro && (
                            <span
                              data-testid={`plugin-pro-badge-${pkg.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500/30 to-rose-500/30 px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-amber-100 ring-1 ring-amber-300/50"
                            >
                              <Crown className="h-2.5 w-2.5" />
                              Pro
                            </span>
                          )}
                        </span>
                        <span className="block font-body text-[11px] leading-snug text-white/55">
                          {pkg.description}
                        </span>
                        {proLocked && (
                          <span
                            data-testid={`plugin-upgrade-cta-${pkg.id}`}
                            className="mt-1 inline-block font-mono text-[9.5px] uppercase tracking-wider text-amber-200/85"
                          >
                            Upgrade to unlock · $9.99/mo
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* iter-118 — Library / Market tab toggle. Personal uploads
                  on the left, public marketplace on the right. The Upload
                  control sits on the library side because you can only
                  upload to your own library. */}
              <div className="mt-5 mb-2 flex items-center justify-between gap-2">
                <div role="tablist" className="inline-flex rounded-full border border-white/10 bg-white/[0.03] p-0.5 font-mono text-[9px] uppercase tracking-[0.18em]">
                  <button
                    type="button"
                    data-testid="community-plugin-tab-library"
                    role="tab"
                    aria-selected={libraryTab === "library"}
                    onClick={() => setLibraryTab("library")}
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 transition ${
                      libraryTab === "library"
                        ? "bg-emerald-500/[0.22] text-emerald-100 ring-1 ring-emerald-400/40"
                        : "text-white/55 hover:text-white"
                    }`}
                  >
                    <FileCode2 className="h-3 w-3" />
                    Library · {communityPlugins.length}
                  </button>
                  <button
                    type="button"
                    data-testid="community-plugin-tab-market"
                    role="tab"
                    aria-selected={libraryTab === "market"}
                    onClick={() => setLibraryTab("market")}
                    className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 transition ${
                      libraryTab === "market"
                        ? "bg-fuchsia-500/[0.22] text-fuchsia-100 ring-1 ring-fuchsia-400/40"
                        : "text-white/55 hover:text-white"
                    }`}
                  >
                    <Store className="h-3 w-3" />
                    Market{marketPlugins.length > 0 ? ` · ${marketPlugins.length}` : ""}
                  </button>
                </div>
                {libraryTab === "library" ? (
                  <button
                    type="button"
                    data-testid="community-plugin-upload-toggle"
                    onClick={() => setUploadOpen((v) => !v)}
                    className="rounded-full border border-emerald-400/30 bg-emerald-500/[0.12] px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-emerald-100 transition hover:bg-emerald-500/[0.22]"
                  >
                    <Upload className="mr-1 inline h-2.5 w-2.5" />
                    {uploadOpen ? "Cancel" : "Upload .js or .zip"}
                  </button>
                ) : (
                  <select
                    data-testid="community-market-sort"
                    value={marketSort}
                    onChange={(e) => setMarketSort(e.target.value)}
                    className="rounded-full border border-fuchsia-400/30 bg-fuchsia-500/[0.10] px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-fuchsia-100 outline-none transition hover:bg-fuchsia-500/[0.18]"
                  >
                    <option value="featured" className="bg-zinc-900">Featured this week</option>
                    <option value="stars" className="bg-zinc-900">Most starred</option>
                    <option value="installs" className="bg-zinc-900">Most installed</option>
                    <option value="recent" className="bg-zinc-900">Most recent</option>
                  </select>
                )}
              </div>

              {libraryTab === "library" && uploadOpen && (
                <div data-testid="community-plugin-upload-form" className="mb-3 rounded-xl border border-emerald-400/20 bg-emerald-500/[0.04] p-3 space-y-2">
                  <input
                    data-testid="community-plugin-file"
                    ref={uploadFileRef}
                    type="file"
                    accept=".js,.zip,application/javascript,text/javascript,application/zip,application/x-zip-compressed"
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 font-mono text-[10px] text-white file:mr-2 file:rounded file:border-0 file:bg-emerald-500/30 file:px-2 file:py-1 file:text-[10px] file:text-emerald-100 file:cursor-pointer"
                  />
                  <input
                    data-testid="community-plugin-label"
                    type="text"
                    placeholder="Display label · auto-filled if .zip has manifest"
                    value={uploadLabel}
                    onChange={(e) => setUploadLabel(e.target.value)}
                    maxLength={80}
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 font-mono text-[11px] text-white placeholder:text-white/30 focus:border-emerald-400/50 focus:outline-none"
                  />
                  <input
                    data-testid="community-plugin-processor-name"
                    type="text"
                    placeholder="processorName · auto-filled if .zip has manifest"
                    value={uploadProcessorName}
                    onChange={(e) => setUploadProcessorName(e.target.value)}
                    maxLength={64}
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 font-mono text-[11px] text-white placeholder:text-white/30 focus:border-emerald-400/50 focus:outline-none"
                  />
                  <input
                    data-testid="community-plugin-description"
                    type="text"
                    placeholder="Description (optional)"
                    value={uploadDescription}
                    onChange={(e) => setUploadDescription(e.target.value)}
                    maxLength={400}
                    className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 font-mono text-[11px] text-white placeholder:text-white/30 focus:border-emerald-400/50 focus:outline-none"
                  />
                  <details className="text-[10px]">
                    <summary className="cursor-pointer font-mono text-[9.5px] uppercase tracking-wider text-white/55 hover:text-white/85">
                      Advanced · param schema (optional JSON)
                    </summary>
                    <textarea
                      data-testid="community-plugin-param-schema"
                      value={uploadParamSchema}
                      onChange={(e) => setUploadParamSchema(e.target.value)}
                      rows={4}
                      placeholder='[{"key":"mix","label":"Mix","min":0,"max":1,"step":0.01,"defaultValue":0.5,"unit":""}]'
                      className="mt-1 w-full rounded-lg border border-white/10 bg-black/30 px-2 py-1.5 font-mono text-[10px] text-white placeholder:text-white/25 focus:border-emerald-400/40 focus:outline-none"
                    />
                  </details>
                  <button
                    type="button"
                    data-testid="community-plugin-upload-submit"
                    onClick={handleUpload}
                    disabled={busy === "upload"}
                    className="w-full rounded-lg bg-emerald-500/[0.22] py-2 font-mono text-[11px] uppercase tracking-wider text-emerald-100 ring-1 ring-emerald-400/40 transition hover:bg-emerald-500/[0.32] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {busy === "upload" ? "Uploading…" : "Upload + add to library"}
                  </button>
                  <p className="font-mono text-[9px] uppercase tracking-wider text-white/40">
                    .js or .zip · ≤ 256 KB JS · drop a WAM v2 bundle with <code className="text-emerald-200">descriptor.json</code> or <code className="text-emerald-200">elpm.json</code> to auto-fill fields
                  </p>
                </div>
              )}

              {libraryTab === "library" && communityLoading && communityPlugins.length === 0 && (
                <p className="font-mono text-[10px] text-white/40">Loading your library…</p>
              )}
              {libraryTab === "library" && !communityLoading && communityPlugins.length === 0 && !uploadOpen && (
                <p data-testid="community-plugin-empty" className="font-mono text-[10px] text-white/40">
                  No plugins yet. Click "Upload .js or .zip" above to add your first one — or switch to the Market tab to browse public plugins.
                </p>
              )}
              {libraryTab === "library" && communityPlugins.length > 0 && (
                <div data-testid="community-plugin-list" className="grid grid-cols-1 gap-2">
                  {communityPlugins.map((cp) => (
                    <div
                      key={cp.id}
                      data-testid={`community-plugin-${cp.id}`}
                      className="group flex items-center gap-2 rounded-xl border border-white/[0.08] bg-white/[0.03] p-2 hover:bg-white/[0.06] transition"
                    >
                      <button
                        type="button"
                        data-testid={`community-plugin-add-${cp.id}`}
                        onClick={() => handleAddCommunity(cp)}
                        disabled={busy === cp.id}
                        className="flex flex-1 items-center gap-2 text-left disabled:opacity-50"
                      >
                        <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg bg-emerald-500/[0.18] ring-1 ring-emerald-400/30 text-emerald-200">
                          <Plus className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="block truncate font-display text-[13px] font-semibold text-white">{cp.label}</span>
                            {cp.is_public && (
                              <span data-testid={`community-plugin-public-badge-${cp.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-fuchsia-500/[0.18] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-fuchsia-200 ring-1 ring-fuchsia-400/30">
                                <Globe className="h-2 w-2" /> public
                              </span>
                            )}
                            {cp.star_count > 0 && (
                              <span className="inline-flex items-center gap-0.5 font-mono text-[9px] text-amber-200">
                                <Star className="h-2 w-2 fill-current" /> {cp.star_count}
                              </span>
                            )}
                          </span>
                          <span className="block truncate font-mono text-[9.5px] text-white/40">
                            {cp.processor_name} · {(cp.size_bytes / 1024).toFixed(1)} KB
                            {cp.description ? ` · ${cp.description}` : ""}
                          </span>
                        </span>
                      </button>
                      {/* iter-118 — visibility toggle. Owner can flip
                          private↔public. Always visible (not on-hover) so
                          users discover the marketplace publishing flow. */}
                      <button
                        type="button"
                        data-testid={`community-plugin-visibility-${cp.id}`}
                        onClick={() => handleToggleVisibility(cp)}
                        title={cp.is_public ? "Make private (remove from marketplace)" : "Publish to marketplace"}
                        className={`rounded-md border p-1.5 transition ${
                          cp.is_public
                            ? "border-fuchsia-400/30 bg-fuchsia-500/[0.15] text-fuchsia-200 hover:bg-fuchsia-500/[0.25]"
                            : "border-white/10 bg-white/[0.04] text-white/55 hover:border-fuchsia-400/30 hover:bg-fuchsia-500/[0.12] hover:text-fuchsia-200"
                        }`}
                      >
                        {cp.is_public ? <Globe className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      </button>
                      <button
                        type="button"
                        data-testid={`community-plugin-delete-${cp.id}`}
                        onClick={() => handleDeleteCommunity(cp)}
                        title="Delete from library"
                        className="rounded-md border border-white/10 bg-white/[0.04] p-1.5 text-white/45 opacity-0 transition group-hover:opacity-100 hover:border-rose-400/30 hover:bg-rose-500/[0.12] hover:text-rose-200"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* iter-118 — Market tab. Lists public plugins from the
                  community. Star toggles a heart; Add loads onto the
                  current channel + increments the install counter. */}
              {libraryTab === "market" && marketLoading && (
                <p className="font-mono text-[10px] text-white/40">Loading marketplace…</p>
              )}
              {libraryTab === "market" && !marketLoading && marketPlugins.length === 0 && (
                <p data-testid="community-market-empty" className="font-mono text-[10px] text-white/40">
                  The marketplace is empty. Be the first to publish — switch back to Library, upload a plugin, then click the globe icon to make it public.
                </p>
              )}
              {libraryTab === "market" && marketPlugins.length > 0 && (() => {
                // iter-119 — Featured-this-week rail: top 3 plugins by
                // installs_7d (only when at least one has momentum). Lives
                // ABOVE the main list so it always appears, regardless of
                // the user's chosen sort. Skipping when nobody's installed
                // anything recently keeps the rail honest.
                const featured = [...marketPlugins]
                  .filter((p) => (p.installs_7d || 0) > 0)
                  .sort((a, b) => (b.installs_7d || 0) - (a.installs_7d || 0))
                  .slice(0, 3);
                if (featured.length === 0) return null;
                return (
                  <div data-testid="community-market-featured" className="mb-3 rounded-xl border border-orange-400/[0.22] bg-gradient-to-br from-orange-500/[0.08] to-fuchsia-500/[0.05] p-2.5">
                    <p className="mb-1.5 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-orange-200">
                      <Flame className="h-3 w-3" />
                      Featured this week
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {featured.map((cp) => (
                        <button
                          key={`featured-${cp.id}`}
                          type="button"
                          data-testid={`community-market-featured-${cp.id}`}
                          onClick={() => handleAddCommunity(cp, { incrementInstall: true })}
                          disabled={busy === cp.id}
                          title={`${cp.label} · ${cp.installs_7d} installs this week · click to add to CH ${channelIndex + 1}`}
                          className="flex items-center gap-1.5 rounded-lg border border-orange-400/[0.22] bg-black/30 px-2 py-1 transition hover:border-orange-400/40 hover:bg-black/45 disabled:opacity-50"
                        >
                          <span className="font-display text-[11px] text-white">{cp.label}</span>
                          <span className="inline-flex items-center gap-0.5 font-mono text-[9px] text-orange-200">
                            <Flame className="h-2.5 w-2.5" />{cp.installs_7d}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {libraryTab === "market" && marketPlugins.length > 0 && (
                <div data-testid="community-market-list" className="grid grid-cols-1 gap-2">
                  {marketPlugins.map((cp) => (
                    <div
                      key={cp.id}
                      data-testid={`community-market-${cp.id}`}
                      className="flex items-center gap-2 rounded-xl border border-fuchsia-400/[0.12] bg-fuchsia-500/[0.03] p-2 hover:bg-fuchsia-500/[0.08] transition"
                    >
                      <button
                        type="button"
                        data-testid={`community-market-add-${cp.id}`}
                        onClick={() => handleAddCommunity(cp, { incrementInstall: true })}
                        disabled={busy === cp.id}
                        className="flex flex-1 items-center gap-2 text-left disabled:opacity-50"
                      >
                        <span className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-lg bg-fuchsia-500/[0.18] ring-1 ring-fuchsia-400/30 text-fuchsia-200">
                          <Plus className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-1.5">
                            <span className="truncate font-display text-[13px] font-semibold text-white">{cp.label}</span>
                            {/* iter-119 — NEW pill for plugins ≤ 24h old.
                                Created_at is ISO; we eyeball the diff in
                                ms vs 24h instead of pulling a date lib. */}
                            {cp.created_at && (Date.now() - new Date(cp.created_at).getTime() < 24 * 60 * 60 * 1000) && (
                              <span data-testid={`community-market-new-${cp.id}`} className="rounded-full bg-emerald-500/[0.22] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-emerald-100 ring-1 ring-emerald-400/40">
                                New
                              </span>
                            )}
                            {/* iter-119 — Featured-this-week velocity chip. */}
                            {cp.installs_7d > 0 && (
                              <span data-testid={`community-market-velocity-${cp.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-orange-500/[0.18] px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-orange-200 ring-1 ring-orange-400/40">
                                <Flame className="h-2 w-2" />{cp.installs_7d}/wk
                              </span>
                            )}
                          </span>
                          <span className="flex items-center gap-2 font-mono text-[9.5px] text-white/40">
                            <span className="truncate">{cp.processor_name}</span>
                            <span className="text-amber-200/85 flex items-center gap-0.5">
                              <Star className="h-2.5 w-2.5 fill-current" />{cp.star_count}
                            </span>
                            <span className="text-cyan-200/75 flex items-center gap-0.5">
                              <TrendingUp className="h-2.5 w-2.5" />{cp.install_count}
                            </span>
                          </span>
                          {cp.description && (
                            <span className="block truncate font-mono text-[9.5px] text-white/30">{cp.description}</span>
                          )}
                        </span>
                      </button>
                      <button
                        type="button"
                        data-testid={`community-market-star-${cp.id}`}
                        onClick={() => handleToggleStar(cp)}
                        title={cp.is_starred ? "Unstar" : "Star"}
                        className={`rounded-md border p-1.5 transition ${
                          cp.is_starred
                            ? "border-amber-400/40 bg-amber-500/[0.18] text-amber-200"
                            : "border-white/10 bg-white/[0.04] text-white/45 hover:border-amber-400/30 hover:bg-amber-500/[0.12] hover:text-amber-200"
                        }`}
                      >
                        <Star className={`h-3 w-3 ${cp.is_starred ? "fill-current" : ""}`} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-5 mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-200/80">
                <Link2 className="h-3 w-3" />
                Custom URL
              </p>
              <div className="space-y-2">
                <input
                  data-testid="plugin-custom-url"
                  type="text"
                  placeholder="https://example.com/my-processor.js"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 font-mono text-[11px] text-white placeholder:text-white/30 focus:border-cyan-400/40 focus:outline-none"
                />
                <input
                  data-testid="plugin-custom-name"
                  type="text"
                  placeholder="processorName (optional)"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 font-mono text-[11px] text-white placeholder:text-white/30 focus:border-cyan-400/40 focus:outline-none"
                />
                <button
                  type="button"
                  data-testid="plugin-custom-add"
                  onClick={handleAddCustom}
                  disabled={busy === "custom" || !customUrl.trim()}
                  className="w-full rounded-lg bg-cyan-500/[0.18] py-2 font-mono text-[11px] uppercase tracking-wider text-cyan-100 ring-1 ring-cyan-400/30 transition hover:bg-cyan-500/[0.28] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {busy === "custom" ? "Loading…" : "Add custom plugin"}
                </button>
              </div>
            </section>

            {/* Loaded list */}
            <section
              data-testid="plugin-loaded-list"
              className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4"
            >
              <p className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-200/80">
                <Plug className="h-3 w-3" />
                Loaded · {loaded.length}
              </p>
              {loaded.length === 0 ? (
                <p data-testid="plugin-loaded-empty" className="rounded-lg border border-dashed border-white/[0.08] bg-black/20 p-3 font-mono text-[11px] uppercase tracking-wider text-white/40">
                  No plugins loaded — pick one on the left.
                </p>
              ) : (
                <div className="space-y-3">
                  {loaded.map((entry, entryIdx) => (
                    <LoadedPluginCard
                      // entryIdx + bump make the key totally collision-safe even
                      // if a future engine bug ever produces duplicate ids.
                      key={`${entry.id}-${entryIdx}-${bump}`}
                      entry={entry}
                      channelIndex={channelIndex}
                      engine={engine}
                      onRemoved={() => setBump((b) => b + 1)}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          <div className="border-t border-white/[0.07] bg-black/30 px-6 py-3 font-mono text-[10px] uppercase tracking-wider text-white/40">
            All plugins receive `transport` postMessages with tempo, beat + bar — your processor reads
            <code className="mx-1 rounded bg-white/[0.05] px-1 py-0.5 text-fuchsia-200">e.data.tempo</code>
            to stay in sync.
          </div>
        </motion.div>
      </motion.div>
      {/* Pro upgrade upsell — opened when a free user taps a Pro-locked plugin. */}
      <PricingModal key="plugin-pricing-overlay" open={pricingOpen} onClose={() => setPricingOpen(false)} />
    </AnimatePresence>
  );
}
